/**
 * MetricExplanationModalView
 *
 * Pure presentation component for the metric explanation modal.
 * On web: renders via portal scoped to the player page content area.
 * On native: renders via RN Modal for reliable full-screen overlay — unless
 * the caller is itself a presented modal route (`hostIsModalRoute`), in which
 * case the content renders directly in that route (SE-8770, below).
 * @remarks This is a View component - contains no business logic, hooks, or state
 *
 * Safe area (SE-7396): the header sits inside a `SafeAreaPad` fed by the
 * `topInset` prop the container resolves, NOT inside a nested `SafeAreaView`.
 * RN's `Modal` mounts in a separate native hierarchy on iOS, so an in-content
 * `SafeAreaView` reads a 0/racy inset on first paint and the header plus its
 * close button render under the status bar / Dynamic Island (the X becomes
 * untappable). Container reads the inset, the atom owns the style.
 *
 * Single presentation (SE-8770): the breakdown route is already a native
 * `transparentModal` with a fade. Presenting an RN `Modal` inside it started a
 * second native presentation while the first was still animating, and on a
 * first open (cold roster query re-rendering mid-presentation) the header was
 * laid out under the status bar. When `hostIsModalRoute` is set the route is
 * the presentation: the same backdrop, card, header and X render in place, and
 * close still goes through `onClose`. Callers that are not a route (the
 * player page centre, compare players, the preview modal) keep the RN Modal.
 *
 * Both wrapper Pressables are marked non-accessible. React Native treats a
 * pressable as one accessibility element by default and flattens everything
 * under it, which collapsed this whole modal into a single leaf: the header
 * and close button had no identity of their own, so neither assistive
 * technology nor native automation could reach them. Opting the wrappers out
 * exposes the title and the close button individually; it changes no touch
 * behaviour, since the backdrop and the card still receive their presses.
 */

import { TEST_ID_METRIC_EXPLANATION_MODAL } from "e2e/testId";
import { XIcon } from "lucide-react-native";
import React from "react";
import { Platform } from "react-native";
import {
  MetricExplanation,
  MetricExplanationModalProps,
} from "@/types/metricExplanationTypes";

import MetricExplanationContent from "@/components/organisms/MetricExplanationContent";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { NativeModal } from "@/components/atoms/NativeModal";
import { GorhomPortal, PLAYER_DETAIL_PORTAL } from "@/components/atoms/Portal";
import { Pressable } from "@/components/atoms/Pressable";
import { SafeAreaPad } from "@/components/atoms/SafeAreaPad";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Text } from "@/components/atoms/Text";

/**
 * Props for MetricExplanationModalView component
 */
interface MetricExplanationModalViewProps extends Omit<
  MetricExplanationModalProps,
  "metricId"
> {
  explanation: MetricExplanation;
  isDark: boolean;
  /** Resolved top safe-area inset in px (0 on web) — see the file remarks. */
  readonly topInset: number;
}

/**
 * Stops event propagation to prevent backdrop close when pressing modal content
 * @param e - Press event object
 * @param e.stopPropagation - Function to stop event bubbling
 */
const handleStopPropagation = (e: { stopPropagation: () => void }) => {
  e.stopPropagation();
};

/**
 * Shared modal inner content (backdrop + card) used by both renderers
 * @param root0 - Component props
 * @param root0.onClose - Callback invoked when modal should close
 * @param root0.explanation - Metric explanation data with title and methodology
 * @param root0.metricValue - Numeric or string value of the metric
 * @param root0.rosterValues - Array of roster comparison values
 * @param root0.player - Player data fragment
 * @param root0.listPlayerSameTeamAndPosition - Players in same team/position for comparison
 * @param root0.isFetchingListPlayerSameTeamAndPosition - Loading state for comparison data
 * @param root0.isDark - Whether dark theme is active
 * @param root0.topInset - Resolved top safe-area inset in px; the Container guarantees 0 on web
 */
const ModalInnerContent = ({
  onClose,
  explanation,
  metricValue,
  rosterValues,
  player,
  listPlayerSameTeamAndPosition,
  isFetchingListPlayerSameTeamAndPosition,
  isDark,
  topInset,
}: Omit<MetricExplanationModalViewProps, "isOpen" | "hostIsModalRoute">) => (
  <Pressable
    className="flex-1 items-center justify-center bg-black/50"
    onPress={onClose}
    accessible={false}
  >
    {/* Modal container — full screen on native, card on web */}
    <Pressable
      onPress={handleStopPropagation}
      className="web:w-full web:max-w-150 web:max-h-4/5 web:rounded-lg web:overflow-hidden web:border web:border-outline-subtle bg-surface-base native:size-full"
      accessible={false}
    >
      <Box className="flex-1">
        {/* Header — SafeAreaPad clears the status bar by the resolved inset */}
        <SafeAreaPad
          top={topInset}
          testID={TEST_ID_METRIC_EXPLANATION_MODAL.HEADER}
        >
          <Box className="flex-row items-center justify-between border-b border-outline-subtle p-4">
            <Stack direction="horizontal" className="flex-1 items-center gap-2">
              <Text
                variant="title"
                className={isDark ? "text-white" : "text-content-primary"}
              >
                {explanation.title}
              </Text>
            </Stack>
            <Pressable
              testID={TEST_ID_METRIC_EXPLANATION_MODAL.CLOSE}
              onPress={onClose}
              hitSlop={12}
            >
              <Box className="rounded-full bg-surface-action p-2">
                <Icon as={XIcon} size="md" className="text-content-subtle" />
              </Box>
            </Pressable>
          </Box>
        </SafeAreaPad>

        {/* Body */}
        <ScrollView
          className="bg-surface-base p-4"
          showsVerticalScrollIndicator
          contentContainerStyle={{ paddingBottom: 16 }}
        >
          <MetricExplanationContent
            explanation={explanation}
            metricValue={metricValue}
            rosterValues={rosterValues}
            player={player}
            listPlayerSameTeamAndPosition={listPlayerSameTeamAndPosition}
            isFetchingListPlayerSameTeamAndPosition={
              isFetchingListPlayerSameTeamAndPosition
            }
            isDark={isDark}
          />
        </ScrollView>
      </Box>
    </Pressable>
  </Pressable>
);

/**
 * View component for metric explanation modal
 * @param props - Component props
 * @param props.isOpen - Whether the modal is currently visible
 * @param props.onClose - Callback invoked when modal close button is pressed
 * @param props.explanation - Metric explanation object with title, description, and methodology
 * @param props.metricValue - Numeric or string value of the metric to display
 * @param props.rosterValues - Array of metric values for team roster comparison
 * @param props.player - Player data including positions and categorical scores
 * @param props.listPlayerSameTeamAndPosition - Array of players in same team and position for comparison
 * @param props.isFetchingListPlayerSameTeamAndPosition - Whether comparison players data is loading
 * @param props.isDark - Whether dark theme is active (affects background and text colors)
 * @param props.topInset - Resolved top safe-area inset in px; the Container guarantees 0 on web, where the modal is a centered portal card
 * @param props.hostIsModalRoute - The caller is already a presented native modal route, so render in place instead of presenting an RN Modal (SE-8770)
 */
const MetricExplanationModalView: React.FC<MetricExplanationModalViewProps> =
  Platform.OS === "web"
    ? ({
        isOpen,
        onClose,
        explanation,
        metricValue,
        rosterValues,
        player,
        listPlayerSameTeamAndPosition,
        isFetchingListPlayerSameTeamAndPosition,
        isDark,
        topInset,
      }) =>
        isOpen ? (
          <GorhomPortal name={PLAYER_DETAIL_PORTAL}>
            <Pressable className="absolute inset-0" onPress={onClose}>
              <ModalInnerContent
                onClose={onClose}
                explanation={explanation}
                metricValue={metricValue}
                rosterValues={rosterValues}
                player={player}
                listPlayerSameTeamAndPosition={listPlayerSameTeamAndPosition}
                isFetchingListPlayerSameTeamAndPosition={
                  isFetchingListPlayerSameTeamAndPosition
                }
                isDark={isDark}
                topInset={topInset}
              />
            </Pressable>
          </GorhomPortal>
        ) : null
    : ({
        isOpen,
        onClose,
        explanation,
        metricValue,
        rosterValues,
        player,
        listPlayerSameTeamAndPosition,
        isFetchingListPlayerSameTeamAndPosition,
        isDark,
        topInset,
        hostIsModalRoute,
      }) => {
        if (!isOpen) return null;
        const content = (
          <ModalInnerContent
            onClose={onClose}
            explanation={explanation}
            metricValue={metricValue}
            rosterValues={rosterValues}
            player={player}
            listPlayerSameTeamAndPosition={listPlayerSameTeamAndPosition}
            isFetchingListPlayerSameTeamAndPosition={
              isFetchingListPlayerSameTeamAndPosition
            }
            isDark={isDark}
            topInset={topInset}
          />
        );
        if (hostIsModalRoute) return content;
        return (
          <NativeModal
            visible={isOpen}
            transparent
            animationType="fade"
            onRequestClose={onClose}
          >
            {content}
          </NativeModal>
        );
      };

MetricExplanationModalView.displayName = "MetricExplanationModalView";

export default React.memo(MetricExplanationModalView);
